"use client";

import { useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

interface ErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
    const t = useTranslations("errors.runtime");

    useEffect(() => {
        console.error(error);
    }, [error]);

    const stackLines = useMemo(() => {
        if (!error.stack) return [];
        return error.stack
            .split("\n")
            .slice(1, 5)
            .map((l) => l.trim())
            .filter(Boolean);
    }, [error.stack]);

    return (
        <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-20">
            <div
                className="glitch-text font-mono text-7xl sm:text-8xl text-foreground select-none"
                data-text={t("code")}
                aria-label={t("title")}
            >
                {t("code")}
            </div>
            <h1 className="mt-4 text-2xl font-medium tracking-tight text-foreground">
                {t("title")}
            </h1>

            <div className="scanlines mt-8 rounded-md border border-border bg-card/60 p-4 font-mono text-xs leading-relaxed text-muted-foreground sm:text-sm">
                <div>
                    <span className="text-foreground">$</span> render{" "}
                    <span className="text-foreground">&lt;Page /&gt;</span>
                </div>
                <div className="mt-1">
                    <span className="rounded bg-[color:var(--destructive,#ff3860)]/15 px-1.5 py-0.5 font-semibold text-[color:var(--destructive,#ff3860)]">
                        [ERROR]
                    </span>{" "}
                    {t("shellError")}
                </div>
                <div className="mt-1 break-words text-foreground/80">
                    → {error.message || "unknown"}
                </div>
                {stackLines.length > 0 && (
                    <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-words text-[11px] opacity-60">
                        {stackLines.join("\n")}
                        {"\n"}
                        {t("stackHint")}
                    </pre>
                )}
                {error.digest && (
                    <div className="mt-2 opacity-70">
                        {t("digest")}={error.digest}
                    </div>
                )}
                <div className="mt-3">
                    <span className="text-foreground">$</span>
                    <span className="term-caret" />
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-4 font-mono text-xs">
                <button
                    type="button"
                    onClick={reset}
                    className="text-foreground underline-offset-4 hover:underline"
                >
                    $ {t("retry")}
                </button>
                <Link
                    href="/"
                    className="text-foreground underline-offset-4 hover:underline"
                >
                    $ {t("home")}
                </Link>
            </div>
        </div>
    );
}
